import { FunctionComponent, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faXmark, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { EmailTemplate } from '@spike/email-marketing-model';
import Button from 'components/UI/V3/Button/Button';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { Checkbox } from 'components/UI/V3/Forms/Checkbox/Checkbox';
import EmailTemplatePicker from './EmailTemplatePicker';
import EmailTemplateContent from './EmailTemplateContent';
import { useMarketplace } from 'hooks';
import styles from './EmailSidebar.module.scss';

export interface EmailStepConfig {
    name: string;
    emailTemplateId: number | null;
    subjectLine: string;
    previewText: string;
    senderName: string;
    senderEmail: string;
    useAsReplyTo: boolean;
    skipRecentlyEmailed: boolean;
}

interface Props {
    initialConfig?: EmailStepConfig;
    onSave?: (config: EmailStepConfig) => void;
    onCancel?: () => void;
    onConfigChange?: (config: EmailStepConfig) => void;
}

// Steps saved before the sender defaulted to the marketplace carry these
// literals, so they have to read as "never set" rather than as a real value.
const LEGACY_SENDER_DEFAULTS = ['Business Name', 'business@domain.com'];

const stored = (value?: string): string => {
    const trimmed = value?.trim() ?? '';

    return LEGACY_SENDER_DEFAULTS.includes(trimmed) ? '' : trimmed;
};

const EmailSidebar: FunctionComponent<Props> = ({ initialConfig, onSave, onCancel, onConfigChange }) => {
    const marketplace = useMarketplace();
    const defaultName = initialConfig?.name || 'Email #1';
    const [name, setName] = useState(defaultName);
    const [emailTemplateId, setEmailTemplateId] = useState<number | null>(initialConfig?.emailTemplateId ?? null);
    const [showPicker, setShowPicker] = useState(false);
    const [subjectLine, setSubjectLine] = useState(initialConfig?.subjectLine ?? '');
    // Hidden from the sidebar, but still round-tripped so saving never wipes it.
    const [previewText] = useState(initialConfig?.previewText ?? '');
    const [senderName, setSenderName] = useState(stored(initialConfig?.senderName) || marketplace.basics.businessName);
    const [senderEmail, setSenderEmail] = useState(stored(initialConfig?.senderEmail) || marketplace.basics.email);
    const [useAsReplyTo, setUseAsReplyTo] = useState(initialConfig?.useAsReplyTo ?? true);
    const [skipRecentlyEmailed, setSkipRecentlyEmailed] = useState(initialConfig?.skipRecentlyEmailed ?? true);
    const [showSender, setShowSender] = useState(false);

    // useState freezes its initial value, and the marketplace may still be
    // loading when this mounts. Only fills what the user has left empty.
    useEffect(() => {
        setSenderName(prev => prev || marketplace.basics.businessName);
        setSenderEmail(prev => prev || marketplace.basics.email);
    }, [marketplace.basics.businessName, marketplace.basics.email]);

    const config: EmailStepConfig = {
        name,
        emailTemplateId,
        subjectLine,
        previewText,
        senderName,
        senderEmail,
        useAsReplyTo,
        skipRecentlyEmailed
    };

    useEffect(() => {
        onConfigChange?.(config);
    }, [name, emailTemplateId, subjectLine, previewText, senderName, senderEmail, useAsReplyTo, skipRecentlyEmailed]); // eslint-disable-line react-hooks/exhaustive-deps

    // Campaigns pre-fill their subject from the template's subjectDefault and
    // let it be overridden; the email step follows the same rule so picking a
    // template never silently replaces copy the user already wrote.
    const handleSelectTemplate = (template: EmailTemplate) => {
        setEmailTemplateId(template.id ?? null);
        setShowPicker(false);
        if (!subjectLine.trim() && template.subjectDefault) setSubjectLine(template.subjectDefault);
    };

    const handleSave = () => onSave?.(config);

    return (
        <aside className={styles.sidebar}>
            {showPicker && (
                <EmailTemplatePicker
                    selectedId={emailTemplateId}
                    onSelect={handleSelectTemplate}
                    onClose={() => setShowPicker(false)}
                />
            )}

            <div className={styles.body}>
                {/* Header */}
                <div className={styles.nodeHeader}>
                    <div className={styles.iconCircle}>
                        <FontAwesomeIcon icon={faEnvelope} />
                    </div>
                    <div className={styles.headerInfo}>
                        <span className={styles.headerTitle}>Send Email</span>
                    </div>
                    {onCancel && (
                        <button
                            type="button"
                            className={styles.closeBtn}
                            onClick={onCancel}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    )}
                </div>

                {/* Email Name */}
                <div className={styles.section}>
                    <div className={styles.sectionLabel}>Email Name</div>
                    <Input
                        value={name}
                        onChange={e => setName((e.target as HTMLInputElement).value)}
                        placeholder={defaultName}
                        autoFocus={false}
                    />
                </div>

                <hr className={styles.divider} />

                {/* Subject and sender */}
                <div className={styles.section}>
                    <div className={styles.sectionLabel}>Subject and sender</div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>
                            Subject Line <span className={styles.required}>*</span>
                        </label>
                        <Input
                            value={subjectLine}
                            onChange={e => setSubjectLine((e.target as HTMLInputElement).value)}
                            placeholder={`${name || defaultName} Subject`}
                            autoFocus={false}
                        />
                    </div>

                    <button
                        type="button"
                        className={styles.editSenderBtn}
                        onClick={() => setShowSender(v => !v)}
                    >
                        Edit Sender
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={showSender ? styles.chevronUp : styles.chevronDown}
                        />
                    </button>

                    {showSender && (
                        <div className={styles.senderFields}>
                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                    Sender Name <span className={styles.required}>*</span>
                                </label>
                                <Input
                                    value={senderName}
                                    onChange={e => setSenderName((e.target as HTMLInputElement).value)}
                                    placeholder="Business Name"
                                    autoFocus={false}
                                />
                            </div>
                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                    Sender Email <span className={styles.required}>*</span>
                                </label>
                                <Input
                                    value={senderEmail}
                                    onChange={e => setSenderEmail((e.target as HTMLInputElement).value)}
                                    placeholder="business@domain.com"
                                    autoFocus={false}
                                />
                            </div>
                            <div className={styles.replyToRow}>
                                <Checkbox
                                    checked={useAsReplyTo}
                                    onChange={setUseAsReplyTo}
                                />
                                <span className={styles.replyToLabel}>Use as reply-to</span>
                            </div>
                        </div>
                    )}
                </div>

                <hr className={styles.divider} />

                {/* Content */}
                <div className={styles.section}>
                    <EmailTemplateContent
                        templateId={emailTemplateId}
                        onSelect={() => setShowPicker(true)}
                        onRemove={() => setEmailTemplateId(null)}
                    />
                </div>

                <hr className={styles.divider} />

                {/* Skip recently emailed */}
                <div className={styles.section}>
                    <div className={styles.skipItem}>
                        <Checkbox
                            checked={skipRecentlyEmailed}
                            onChange={setSkipRecentlyEmailed}
                        />
                        <div className={styles.skipText}>
                            <span className={styles.skipLabel}>Skip clients another automation emailed recently</span>
                            <span className={styles.skipSub}>
                                Will skip anyone another automation emailed in the last 7 days.
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.footer}>
                <Button
                    label="Save"
                    variant="green"
                    fullWidth
                    disabled={!name.trim() || !subjectLine.trim() || emailTemplateId === null}
                    onClick={handleSave}
                />
            </div>
        </aside>
    );
};

export default EmailSidebar;
